import { useSyncExternalStore } from 'react';
import {
  normalizeSidebarLayout,
  SIDEBAR_GROUPS,
  type SidebarLayout,
} from '../../shared/prefs';
import { api } from './api';

export type { SidebarLayout };

const KEY = 'ss-os-sidebar-layout';

function readCachedLayout(): SidebarLayout | null {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? normalizeSidebarLayout(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

let layout: SidebarLayout | null = readCachedLayout();
const listeners = new Set<() => void>();

function apply(next: SidebarLayout | null): void {
  layout = next;
  try {
    if (next) localStorage.setItem(KEY, JSON.stringify(next));
    else localStorage.removeItem(KEY);
  } catch {
    // The in-memory preference still applies when storage is unavailable.
  }
  listeners.forEach((fn) => fn());
}

export function getSidebarLayout(): SidebarLayout | null {
  return layout;
}

/** Applies the layout locally straight away (optimistic first paint) and
    resolves only once D1 has it — callers must await, so a failed write is
    reported instead of being swallowed behind a "saved" message. */
export function setSidebarLayout(next: SidebarLayout): Promise<void> {
  const normalized = normalizeSidebarLayout(next);
  if (!normalized) throw new Error('Invalid sidebar layout.');
  apply(normalized);
  return api('/api/admin/system/prefs', {
    method: 'PUT',
    json: { sidebarLayout: normalized },
  }).then(() => undefined);
}

/** Reconcile the first-paint cache against D1. Call once per session, after auth. */
export async function syncSidebarLayout(): Promise<void> {
  const before = layout;
  try {
    const prefs = await api<{ sidebarLayout?: unknown }>('/api/admin/system/prefs');
    if (layout !== before) return;
    apply(normalizeSidebarLayout(prefs.sidebarLayout));
  } catch {
    // Offline / expired session: the cache stays until the next sync.
  }
}

export function useSidebarLayout(): SidebarLayout | null {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => layout,
    () => layout,
  );
}

/**
 * Apply a saved order to current code defaults. Unknown ids disappear; new
 * code-defined ids are appended to the end of their own default group.
 */
export function mergeSidebarLayout(
  pref: SidebarLayout | null | undefined,
  defaults: SidebarLayout,
): SidebarLayout {
  const normalizedPref = normalizeSidebarLayout(pref);
  const normalizedDefaults = normalizeSidebarLayout(defaults);
  if (!normalizedDefaults) throw new Error('Invalid default sidebar layout.');

  if (!normalizedPref || SIDEBAR_GROUPS.every((group) => normalizedPref[group].length === 0)) {
    return normalizedDefaults;
  }

  const known = new Set(SIDEBAR_GROUPS.flatMap((group) => normalizedDefaults[group]));
  const merged: SidebarLayout = { growth: [], work: [], money: [], tools: [] };
  const placed = new Set<string>();

  for (const group of SIDEBAR_GROUPS) {
    for (const id of normalizedPref[group]) {
      if (known.has(id) && !placed.has(id)) {
        merged[group].push(id);
        placed.add(id);
      }
    }
  }

  for (const group of SIDEBAR_GROUPS) {
    for (const id of normalizedDefaults[group]) {
      if (!placed.has(id)) {
        merged[group].push(id);
        placed.add(id);
      }
    }
  }

  return merged;
}
